import { max, min, orderBy } from 'lodash';
import type { SearchValueType } from '../common-interfaces';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
  ScrollerData,
  ScrollerDataItem,
} from './continuous-scroller-types';
import { ContinuousScrollerComparisonType } from './continuous-scroller-types';

export function getComparisonType(
  filter: SearchValueType | Array<SearchValueType>,
  comparison?: ContinuousScrollerComparisonType
) {
  if (!comparison) {
    if (!Array.isArray(filter)) {
      if (typeof filter === 'string') {
        return ContinuousScrollerComparisonType.Contains;
      } else if (typeof filter === 'number') {
        return ContinuousScrollerComparisonType.Equals;
      } else if (typeof filter === 'boolean') {
        return ContinuousScrollerComparisonType.Equals;
      } else {
        return ContinuousScrollerComparisonType.Equals;
      }
    } else {
      if (typeof filter[0] === 'string') {
        return ContinuousScrollerComparisonType.Contains;
      } else if (typeof filter[0] === 'number') {
        return ContinuousScrollerComparisonType.Contains;
      } else if (typeof filter[0] === 'boolean') {
        return ContinuousScrollerComparisonType.Equals;
      } else {
        return ContinuousScrollerComparisonType.Between;
      }
    }
  }

  return comparison;
}

export function convertComparisonTypeToString(comparison: ContinuousScrollerComparisonType) {
  switch (comparison) {
    case ContinuousScrollerComparisonType.Contains:
      return 'contains';
    case ContinuousScrollerComparisonType.Equals:
      return '=';
    case ContinuousScrollerComparisonType.Between:
      return 'between';
    case ContinuousScrollerComparisonType.LessThan:
      return '<';
    case ContinuousScrollerComparisonType.LessThanEqualTo:
      return '<=';
    case ContinuousScrollerComparisonType.GreaterThan:
      return '>';
    case ContinuousScrollerComparisonType.GreaterThanEqualTo:
      return '>=';
  }
}

/**
 * Takes the current search filters property and returns only valid search filters; for example a string search filter
 * that is blank will not be returned.
 * @param {Array<ContinuousScrollerFilter>} searchFilters - The current search filters.
 * @return {Array<ContinuousScrollerFilter> | undefined} The valid search filters.
 */
export function getValidSearchFilters(searchFilters?: Array<ContinuousScrollerFilter>) {
  const filteredItems: Array<ContinuousScrollerFilter> = [];
  if (searchFilters && searchFilters.length > 0) {
    for (let i = 0, length = searchFilters.length; i < length; i++) {
      const filter = searchFilters[i];
      if (filter.value !== undefined) {
        if (Array.isArray(filter.value)) {
          const arrayFilterValue: Array<string | number | boolean | Date> = filter.value;

          const arrayFilters: Array<string | number | boolean | Date> = [];
          for (let j = 0, jLength = arrayFilterValue.length; j < jLength; j++) {
            if (arrayFilterValue[j] !== undefined) {
              const arrayFilterArrayValue = arrayFilterValue[j];
              if (typeof arrayFilterArrayValue === 'string' && arrayFilterArrayValue.length > 0) {
                arrayFilters.push(arrayFilterArrayValue);
              } else if (typeof arrayFilterArrayValue === 'number' && !isNaN(arrayFilterArrayValue)) {
                arrayFilters.push(arrayFilterArrayValue);
              } else if (typeof arrayFilterArrayValue === 'boolean' || arrayFilterArrayValue instanceof Date) {
                arrayFilters.push(arrayFilterArrayValue);
              }
            }
          }

          if (arrayFilters.length > 0) {
            filter.value = [];
            filter.value.push(...arrayFilters);
            filteredItems.push(filter);
          }
        } else if (typeof filter.value === 'string') {
          filteredItems.push(filter);
        } else if (typeof filter.value === 'number' && !isNaN(filter.value)) {
          filteredItems.push(filter);
        } else if (typeof filter.value === 'boolean' || filter.value instanceof Date) {
          filteredItems.push(filter);
        }
      }
    }
  }

  return filteredItems.length > 0 ? filteredItems : undefined;
}

/**
 * Checks a particular field in a JSON object against a filter to see if it meets the criteria.
 * @param {SearchValueType} field - The data to check.
 * @param {SearchValueType | Array<SearchValueType>} filter - The information to check the field for.
 * @param {ContinuousScrollerComparisonType} comparison - The type of comparison to perform on the field (e.g. equals, contains or between).
 * @return {boolean} True if the field passes the check else false.
 */
export function checkFieldFilter(
  field: SearchValueType,
  filter: SearchValueType | Array<SearchValueType>,
  comparison?: ContinuousScrollerComparisonType
) {
  const fieldType = typeof field;
  const filterType = Array.isArray(filter) ? typeof filter[0] : typeof filter;

  // I can imagine this causing an issue if for example a filter has the value "2" and it would be compared to a field
  // with the actual value 2; in this case the comparison should take place, but it won't with this code. I suppose I
  // could perform a == comparison and allow JavaScript to perform the type conversion, but I want this to be as
  // performant as possible.
  if (fieldType !== filterType) {
    return false;
  }

  if (!Array.isArray(filter)) {
    if (typeof filter === 'string' && typeof field === 'string') {
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Contains) {
        return field.toLowerCase().includes(filter.toLowerCase());
      } else if (comparison === ContinuousScrollerComparisonType.Equals) {
        return field.toLowerCase() === filter.toLowerCase();
      }

      return false;
    } else if (typeof filter === 'number' && typeof field === 'number') {
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Equals) {
        return field === filter;
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThan) {
        return field > filter;
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThanEqualTo) {
        return field >= filter;
      } else if (comparison === ContinuousScrollerComparisonType.LessThan) {
        return field < filter;
      } else if (comparison === ContinuousScrollerComparisonType.LessThanEqualTo) {
        return field <= filter;
      }

      return false;
    } else if (typeof filter === 'boolean' && typeof field === 'boolean') {
      return field === filter;
    } else if (filter instanceof Date && field instanceof Date) {
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Equals) {
        return field.getTime() === filter.getTime();
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThan) {
        return field.getTime() > filter.getTime();
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThanEqualTo) {
        return field.getTime() >= filter.getTime();
      } else if (comparison === ContinuousScrollerComparisonType.LessThan) {
        return field.getTime() < filter.getTime();
      } else if (comparison === ContinuousScrollerComparisonType.LessThanEqualTo) {
        return field.getTime() <= filter.getTime();
      }

      return false;
    }
  } else {
    if (typeof filter[0] === 'string' && typeof field === 'string') {
      const convertedFilter: Array<string> = filter as Array<string>;
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Contains) {
        return convertedFilter.some((item) => field.toLowerCase().includes(item.toLowerCase()));
      } else if (comparison === ContinuousScrollerComparisonType.Equals) {
        return convertedFilter.some((item) => field.toLowerCase() === item.toLowerCase());
      }

      return false;
    } else if (typeof filter[0] === 'number' && typeof field === 'number') {
      const convertedFilter: Array<number> = filter as Array<number>;
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Equals) {
        return convertedFilter.some((item) => field === item);
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThan) {
        return convertedFilter.some((item) => field > item);
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThanEqualTo) {
        return convertedFilter.some((item) => field >= item);
      } else if (comparison === ContinuousScrollerComparisonType.LessThan) {
        return convertedFilter.some((item) => field < item);
      } else if (comparison === ContinuousScrollerComparisonType.LessThanEqualTo) {
        return convertedFilter.some((item) => field <= item);
      } else if (comparison === ContinuousScrollerComparisonType.Between) {
        // between should only have 2 values 1 for min and 1 for max but just in case we are using the min/max methods
        const minValue = min(convertedFilter);
        const maxValue = max(convertedFilter);
        return minValue !== undefined && maxValue !== undefined ? field >= minValue && field <= maxValue : false;
      }

      return false;
    } else if (typeof filter[0] === 'boolean' && typeof field === 'boolean') {
      const convertedFilter: Array<boolean> = filter as Array<boolean>;
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Equals) {
        return convertedFilter.some((item) => field === item);
      }

      return false;
    } else if (filter[0] instanceof Date && field instanceof Date) {
      const dateFilter: Array<Date> = filter as Array<Date>;
      const convertedFilter: Array<number> = dateFilter.map((date) => date.getTime());
      if (comparison === undefined || comparison === ContinuousScrollerComparisonType.Between) {
        // this comparison will be used for the date range filter
        const minDateValue = min(convertedFilter);
        const maxDateValue = max(convertedFilter);
        return minDateValue !== undefined && maxDateValue !== undefined
          ? field.getTime() >= minDateValue && field.getTime() <= maxDateValue
          : false;
      } else if (comparison === ContinuousScrollerComparisonType.Equals) {
        return convertedFilter.some((item) => field.getTime() === item);
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThan) {
        return convertedFilter.some((item) => field.getTime() > item);
      } else if (comparison === ContinuousScrollerComparisonType.GreaterThanEqualTo) {
        return convertedFilter.some((item) => field.getTime() >= item);
      } else if (comparison === ContinuousScrollerComparisonType.LessThan) {
        return convertedFilter.some((item) => field.getTime() < item);
      } else if (comparison === ContinuousScrollerComparisonType.LessThanEqualTo) {
        return convertedFilter.some((item) => field.getTime() <= item);
      }
    }
  }
}

export function filterData(data: ScrollerData, filters: Array<ContinuousScrollerFilter>) {
  const validFilters = filters.filter((f) =>
    Array.isArray(f.value) ? f.value.length > 0 : f.value !== undefined && f.value !== null
  );

  return validFilters.length > 0
    ? data.filter((item) =>
        validFilters.every((filter) =>
          checkFieldFilter(item[filter.field] as SearchValueType, filter.value, filter.comparison)
        )
      )
    : data;
}

type SortFunctionType = string | ((item: ScrollerDataItem) => string);
export function sortData(data: ScrollerData, sorts: Array<ContinuousScrollerSort>) {
  const sortFields: Array<SortFunctionType> = sorts.map((sort) => {
    const fieldPath = sort.sortPath ? sort.field + sort.sortPath : sort.field;
    let sortProperty: SortFunctionType = fieldPath;
    if (sort.caseSensitive === false) {
      sortProperty = (item: ScrollerDataItem) => (item[fieldPath] as string)?.toLowerCase();
    }
    return sortProperty;
  });
  const sortDirections = sorts.map((sort) => (sort.sortDirection === 'ascending' ? 'asc' : 'desc'));

  return orderBy(data, sortFields, sortDirections);
}
